import React, { useCallback, useState } from "react";

import Form from "antd/es/form";
import Input from "antd/es/input";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { authCommonStyles as commonStyles } from "@mapmycustomers/auth";
import { Alert, Button, FormItem } from "@mapmycustomers/ui";

import PasswordValidation from "@app/component/auth/PasswordValidation";
import Path from "@app/enum/Path";
import validatePasswords, {
  NOTHING_VALID,
  PasswordValidationResult,
} from "@app/util/validatePasswords";

import SignupField from "../Register/enum/SignupField";
import {
  useConfirmPasswordFieldValidationRules,
  usePasswordFieldValidationRules,
} from "../Register/utils/validation-hooks";

import styles from "./ResetPasswordForm.module.scss";

interface Props {
  errorMessage?: string;
  isResetPasswordLoading: boolean;
  onResetPassword: (payload: { code: string; password: string; username: string }) => void;
}

const ResetPasswordForm = ({ errorMessage, isResetPasswordLoading, onResetPassword }: Props) => {
  const intl = useIntl();
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  const username = urlParams.get("username");
  const isValidLink = !!code && !!username;

  const [form] = Form.useForm();

  const passwordFieldValidationRules = usePasswordFieldValidationRules(intl);
  const confirmPasswordFieldValidationRules = useConfirmPasswordFieldValidationRules(intl, form);

  const [passwordValidationResult, setPasswordValidationResult] =
    useState<PasswordValidationResult>(NOTHING_VALID);
  const handleFormFieldsChange = useCallback(() => {
    setPasswordValidationResult(
      validatePasswords(
        form.getFieldValue(SignupField.PASSWORD),
        form.getFieldValue(SignupField.PASSWORD_CONFIRMATION)
      )
    );
  }, [form]);

  const handleResetPassword = useCallback(() => {
    const { password } = form.getFieldsValue();
    if (code && username) {
      onResetPassword({ code, password, username });
    }
  }, [form, onResetPassword, code, username]);

  return (
    <div className={commonStyles.container}>
      {errorMessage && <Alert message={errorMessage} type="error" />}
      {isValidLink ? (
        <Form
          form={form}
          layout="vertical"
          name="forgotPassword"
          onFieldsChange={handleFormFieldsChange}
          onFinish={handleResetPassword}
          preserve={false}
        >
          <p className={commonStyles.title}>
            <FormattedMessage
              id="auth.resetPassword.form.title"
              defaultMessage="Set New Password"
              description="Title of reset new password form"
            />
          </p>
          <p className={commonStyles.description}>
            <FormattedMessage
              id="auth.resetPassword.form.description"
              defaultMessage="Enter your new password"
              description="Title of username field of reset password form"
            />
          </p>
          <FormItem
            colon={false}
            label={intl.formatMessage({
              id: "auth.resetPassword.form.newPassword",
              defaultMessage: "Set Password",
              description: "Title of the New Password field in the reset password form",
            })}
            name={SignupField.PASSWORD}
            requiredMark="optional"
            rules={passwordFieldValidationRules}
          >
            <Input.Password autoComplete="new-password" size="large" spellCheck="false" />
          </FormItem>
          <FormItem
            dependencies={[SignupField.PASSWORD]}
            label={intl.formatMessage({
              id: "auth.resetPassword.form.confirmPassword",
              defaultMessage: "Confirm Password",
              description: "Title of the confirm Password field in the reset password form",
            })}
            name={SignupField.PASSWORD_CONFIRMATION}
            requiredMark="optional"
            rules={confirmPasswordFieldValidationRules}
          >
            <Input.Password autoComplete="new-password" size="large" spellCheck="false" />
          </FormItem>
          <FormItem>
            <PasswordValidation validationResult={passwordValidationResult} />
          </FormItem>
          <FormItem>
            <Button
              className={styles.setPasswordButton}
              disabled={
                !passwordValidationResult.isValid ||
                !passwordValidationResult.passwordsMatch ||
                isResetPasswordLoading
              }
              htmlType="submit"
              loading={isResetPasswordLoading}
              type="primary"
            >
              {isResetPasswordLoading
                ? intl.formatMessage({
                    id: "auth.resetPassword.form.submit.loading",
                    defaultMessage: "Updating...",
                    description:
                      "Title of the submit resetPassword form button after it has been pressed",
                  })
                : intl.formatMessage({
                    id: "auth.resetPassword.form.submit",
                    defaultMessage: "Set Password",
                    description: "Title of the submit forgot password form button",
                  })}
            </Button>
          </FormItem>
        </Form>
      ) : (
        <div className={styles.wrongLinkCard}>
          <p className={commonStyles.title}>
            {intl.formatMessage({
              id: "auth.resetPassword.error.title",
              defaultMessage: "Something went wrong",
              description: "An invalid link error message text",
            })}
          </p>
          <p className={commonStyles.description}>
            {intl.formatMessage({
              id: "auth.resetPassword.error.invalidLink",
              defaultMessage:
                "It seems you’ve clicked on a bad reset password link. Please click directly on the link in the email.",
              description: "An invalid link error message text",
            })}
          </p>
          <Link className={styles.returnLink} to={Path.LOGIN}>
            <Button className={styles.returnButton} type="primary">
              <FormattedMessage
                id="auth.resetPassword.form.loginLink"
                defaultMessage="Return to Login"
                description="Title of the Back to Login Link"
              />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ResetPasswordForm;
